import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AppNamespace, OwnerType, TemplatesService } from '@vendasta/email-builder';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ConfirmationService } from '@vendasta/uikit';
import { Observable, firstValueFrom } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { v4 as uuidV4 } from 'uuid';
import { EMAIL_LIBRARY_BASE_URL_TOKEN, PAGE_ROUTES } from '../../shared';
import { DefaultCardAction, Template } from '../card/card.component';

@Component({
  selector: 'email-cards',
  templateUrl: './email-cards.component.html',
  styleUrls: ['./email-cards.component.scss'],
  standalone: false,
})
export class EmailCardsComponent {
  @Input() ownerType: OwnerType;
  @Input() ownerId: string;
  @Input() appNamespace: AppNamespace;
  @Input() templates: Template[];
  @Input() defaultAction: DefaultCardAction;
  @Input() openAsReadOnly: boolean;
  @Input() userId: string;

  @Output() readOnlyEmailClicked: EventEmitter<Template> = new EventEmitter<Template>();

  constructor(
    private readonly router: Router,
    private readonly templatesService: TemplatesService,
    private readonly confirmationService: ConfirmationService,
    private readonly snackbarService: SnackbarService,
    private readonly translate: TranslateService,
    @Inject(EMAIL_LIBRARY_BASE_URL_TOKEN) private readonly baseUrl$: Observable<string>,
  ) {}

  async handleDuplicateClicked(template: Template): Promise<void> {
    const newTemplate = await firstValueFrom(this.makeDuplicateTemplate(template));
    const parsedJson = JSON.parse(newTemplate.templateJson);
    const subject = newTemplate.templateSubject || parsedJson.templateSubject;
    if (!subject) {
      this.snackbarService.openErrorSnack('EMAIL_LIBRARY.DUPLICATE.ERROR.SUBJECT');
      throw Error(this.translate.instant('EMAIL_LIBRARY.DUPLICATE.ERROR.SUBJECT'));
    }
    await firstValueFrom(
      this.templatesService.createNewTemplate(
        this.ownerType,
        this.ownerId,
        this.appNamespace,
        newTemplate.templateId,
        newTemplate.title,
        newTemplate.templateJson,
        newTemplate.templateHtml,
        subject,
        {
          appNamespace: this.appNamespace,
          templateId: template.templateId,
        },
      ),
    )
      .then(() => {
        if (this.defaultAction !== DefaultCardAction.DUPLICATE) {
          const atIndex = this.templates.findIndex((t) => t.templateId === template.templateId) + 1;
          this.templates.splice(atIndex, 0, newTemplate);
        }
        this.snackbarService.openSuccessSnack('EMAIL_LIBRARY.DUPLICATE.SUCCESS', {
          interpolateTranslateParams: {
            duplicateName: newTemplate.title,
          },
        });
        this.router.navigateByUrl(newTemplate.editorUrl);
      })
      .catch(() => this.snackbarService.openErrorSnack('EMAIL_LIBRARY.DUPLICATE.FAIL'));
  }

  makeDuplicateTemplate(template: Template): Observable<Template> {
    return this.baseUrl$.pipe(
      take(1),
      map((baseUrl) => {
        const templateId = 'TEMPLATE-' + uuidV4();
        const title = template.title + '_copy';
        const createdDate = new Date(Date.now());
        const editorUrl = baseUrl + '/' + PAGE_ROUTES.ROOT.TEMPLATE_SUBROUTES.EDIT(templateId);
        const json = template.templateJson.replace(template.title, title);
        const subject = template.templateSubject;
        return {
          ...template,
          title: title,
          templateId: templateId,
          templateJson: json,
          editorUrl: editorUrl,
          created: createdDate,
          updated: createdDate,
          templateSubject: subject,
        };
      }),
    );
  }

  async handleDeleteClicked(template: Template): Promise<void> {
    const confirmed = await this.confirmationService.confirm({
      title: `${this.translate.instant('COMMON.ACTION_LABELS.DELETE')} ${template?.title}?`,
      text: `${this.translate.instant('EMAIL_LIBRARY.DELETE.CONFIRMATION_MESSAGE')}`,
      closeText: `${this.translate.instant('COMMON.ACTION_LABELS.CANCEL')}`,
      confirmText: `${this.translate.instant('COMMON.ACTION_LABELS.DELETE')}`,
      severity: 'warn',
    });
    if (!confirmed) {
      return;
    }
    await firstValueFrom(
      this.templatesService.deleteTemplate(this.ownerType, this.ownerId, this.appNamespace, template.templateId),
    )
      .then(() => {
        this.templates = this.templates.filter((t) => t.templateId !== template.templateId);
        this.snackbarService.openSuccessSnack('EMAIL_LIBRARY.DELETE.SUCCESS');
      })
      .catch(() => this.snackbarService.openErrorSnack('EMAIL_LIBRARY.DELETE.FAIL'));
  }

  handleReadOnlyEmailClicked(template: Template): void {
    this.readOnlyEmailClicked.emit(template);
  }
}
